import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { auth, db, storage, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaImage,
  FaSave,
  FaTimes,
  FaLightbulb,
  FaGripVertical,
  FaGlobe
} from 'react-icons/fa';
import './AdminExpertise.css';

const AdminExpertise = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expertiseList, setExpertiseList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [activeTab, setActiveTab] = useState('en'); // 'en' or 'fr'
  const [formData, setFormData] = useState({
    // English content
    title_en: '',
    description_en: '',
    features_en: [''],
    // French content
    title_fr: '',
    description_fr: '',
    features_fr: [''],
    // Common fields
    imageUrl: '',
    order: 0
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchExpertise();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchExpertise = async () => {
    try {
      const q = query(collection(db, 'expertise'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setExpertiseList(items);
    } catch (err) {
      console.error('Error fetching expertise:', err);
      setError('Failed to load expertise items');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const fileName = `expertise/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (err) {
      console.error('Upload error:', err);
      if (err.code === 'storage/unauthorized') {
        throw new Error('Permission denied. Please update Firebase Storage rules to allow the "expertise/" folder.');
      }
      throw err;
    }
  };

  const deleteImage = async (imageUrl) => {
    if (imageUrl && imageUrl.includes('firebase')) {
      try {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      } catch (err) {
        console.log('Image deletion error (might not exist):', err);
      }
    }
  };

  const handleFeatureChange = (lang, index, value) => {
    const key = `features_${lang}`;
    const newFeatures = [...formData[key]];
    newFeatures[index] = value;
    setFormData({ ...formData, [key]: newFeatures });
  };

  const addFeature = (lang) => {
    const key = `features_${lang}`;
    setFormData({ ...formData, [key]: [...formData[key], ''] });
  };

  const removeFeature = (lang, index) => {
    const key = `features_${lang}`;
    const newFeatures = formData[key].filter((_, i) => i !== index);
    setFormData({ ...formData, [key]: newFeatures.length ? newFeatures : [''] });
  };

  const resetForm = () => {
    setFormData({
      title_en: '',
      description_en: '',
      features_en: [''],
      title_fr: '',
      description_fr: '',
      features_fr: [''],
      imageUrl: '',
      order: expertiseList.length
    });
    setImagePreview(null);
    setImageFile(null);
    setEditingId(null);
    setShowForm(false);
    setActiveTab('en');
    setError('');
  };

  const handleEdit = (item) => {
    setFormData({
      title_en: item.title_en || item.title || '',
      description_en: item.description_en || item.description || '',
      features_en: item.features_en?.length ? item.features_en : (item.features?.length ? item.features : ['']),
      title_fr: item.title_fr || '',
      description_fr: item.description_fr || '',
      features_fr: item.features_fr?.length ? item.features_fr : [''],
      imageUrl: item.imageUrl || '',
      order: item.order || 0
    });
    setImagePreview(item.imageUrl || null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate at least English content
    if (!formData.title_en.trim() || !formData.description_en.trim()) {
      setError('English title and description are required.');
      setActiveTab('en');
      return;
    }

    setSaving(true);

    try {
      let imageUrl = formData.imageUrl;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const expertiseData = {
        // English content
        title_en: formData.title_en.trim(),
        description_en: formData.description_en.trim(),
        features_en: formData.features_en.filter(f => f.trim() !== ''),
        // French content
        title_fr: formData.title_fr.trim(),
        description_fr: formData.description_fr.trim(),
        features_fr: formData.features_fr.filter(f => f.trim() !== ''),
        // Legacy fields (for backward compatibility)
        title: formData.title_en.trim(),
        description: formData.description_en.trim(),
        features: formData.features_en.filter(f => f.trim() !== ''),
        // Common fields
        imageUrl,
        order: formData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'expertise', editingId), expertiseData);
        setSuccess('Expertise updated successfully!');
      } else {
        expertiseData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'expertise'), expertiseData);
        setSuccess('Expertise added successfully!');
      }

      await fetchExpertise();
      resetForm();
    } catch (err) {
      console.error('Error saving expertise:', err);
      setError(err.message || 'Failed to save expertise. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title_en || item.title}"?`)) {
      return;
    }

    try {
      if (item.imageUrl) {
        await deleteImage(item.imageUrl);
      }
      await deleteDoc(doc(db, 'expertise', item.id));
      setSuccess('Expertise deleted successfully!');
      await fetchExpertise();
    } catch (err) {
      console.error('Error deleting expertise:', err);
      setError('Failed to delete expertise');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-expertise">
      <header className="admin-expertise-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaLightbulb /> Our Expertise</h1>
      </header>

      <main className="admin-expertise-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!showForm ? (
          <>
            <div className="expertise-actions">
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add New Expertise
              </button>
            </div>

            <div className="expertise-list">
              {expertiseList.length === 0 ? (
                <div className="empty-state">
                  <FaLightbulb />
                  <h3>No expertise items yet</h3>
                  <p>Click "Add New Expertise" to create your first item.</p>
                </div>
              ) : (
                expertiseList.map((item, index) => (
                  <div key={item.id} className="expertise-item">
                    <div className="expertise-item-order">
                      <FaGripVertical />
                      <span>{index + 1}</span>
                    </div>
                    <div className="expertise-item-image">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.title_en || item.title} />
                      ) : (
                        <div className="no-image"><FaImage /></div>
                      )}
                    </div>
                    <div className="expertise-item-content">
                      <div className="expertise-item-langs">
                        <span className="lang-badge en">EN: {item.title_en || item.title || 'Not set'}</span>
                        <span className="lang-badge fr">FR: {item.title_fr || 'Not set'}</span>
                      </div>
                      <p>{item.description_en || item.description}</p>
                    </div>
                    <div className="expertise-item-actions">
                      <button onClick={() => handleEdit(item)} className="edit-btn">
                        <FaEdit /> Edit
                      </button>
                      <button onClick={() => handleDelete(item)} className="delete-btn">
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="expertise-form-container">
            <h2>{editingId ? 'Edit Expertise' : 'Add New Expertise'}</h2>

            {/* Language Tabs */}
            <div className="language-tabs">
              <button
                type="button"
                className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
                onClick={() => setActiveTab('en')}
              >
                <FaGlobe /> English
              </button>
              <button
                type="button"
                className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
                onClick={() => setActiveTab('fr')}
              >
                <FaGlobe /> French
              </button>
            </div>

            <form onSubmit={handleSubmit} className="expertise-form">
              {/* English Content */}
              <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (English) *</label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    placeholder="e.g., Robotic Process Automation (RPA)"
                  />
                </div>

                <div className="form-group">
                  <label>Description (English) *</label>
                  <textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                    placeholder="Describe this expertise area in English..."
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label>Features / Key Points (English)</label>
                  {formData.features_en.map((feature, index) => (
                    <div key={index} className="feature-input-row">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange('en', index, e.target.value)}
                        placeholder={`Feature ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature('en', index)}
                        className="remove-feature-btn"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addFeature('en')} className="add-feature-btn">
                    <FaPlus /> Add Feature
                  </button>
                </div>
              </div>

              {/* French Content */}
              <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (French)</label>
                  <input
                    type="text"
                    value={formData.title_fr}
                    onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                    placeholder="e.g., Automatisation Robotisée des Processus (RPA)"
                  />
                </div>

                <div className="form-group">
                  <label>Description (French)</label>
                  <textarea
                    value={formData.description_fr}
                    onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                    placeholder="Décrivez ce domaine d'expertise en français..."
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label>Features / Key Points (French)</label>
                  {formData.features_fr.map((feature, index) => (
                    <div key={index} className="feature-input-row">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange('fr', index, e.target.value)}
                        placeholder={`Fonctionnalité ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature('fr', index)}
                        className="remove-feature-btn"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addFeature('fr')} className="add-feature-btn">
                    <FaPlus /> Add Feature
                  </button>
                </div>
              </div>

              {/* Common Fields (always visible) */}
              <div className="common-fields">
                <div className="form-group">
                  <label>Image (shared for both languages)</label>
                  <div className="image-upload-area">
                    {imagePreview ? (
                      <div className="image-preview">
                        <img src={imagePreview} alt="Preview" />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setImageFile(null);
                            setFormData({ ...formData, imageUrl: '' });
                          }}
                          className="remove-image-btn"
                        >
                          <FaTimes /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FaImage />
                        <span>Click to upload image</span>
                        <small>Max size: 5MB (JPG, PNG, WebP)</small>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          hidden
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  <FaTimes /> Cancel
                </button>
                <button type="submit" disabled={saving} className="save-btn">
                  {saving ? (
                    <><FaSpinner className="spinner" /> Saving...</>
                  ) : (
                    <><FaSave /> {editingId ? 'Update' : 'Save'} Expertise</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminExpertise;
